import { gaeApp } from '../components/helpers/Analytics';
import { isAppOnline } from './connectivity';
import { createBlobFromUrl } from './fileUtils';

const DB_NAME = 'paperfly-templates';
const DB_VERSION = 2;
const STORE_NAME = 'templates';

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error || new Error('Failed to open template cache'));
    };
  });

  return dbPromise;
};

const idbRequest = request =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const isHttpUrl = value =>
  typeof value === 'string' && /^https?:\/\//i.test(value);

const addUrl = (urls, value) => {
  if (isHttpUrl(value)) {
    urls.add(value);
  }
};

const walkFormStructure = (nodes, urls) => {
  if (!nodes) return;
  const list = Array.isArray(nodes) ? nodes : [nodes];

  list.forEach(node => {
    if (!node || typeof node !== 'object') return;

    // formStructure is pages of elements: [[{...}], [{...}]]
    if (Array.isArray(node)) {
      walkFormStructure(node, urls);
      return;
    }

    addUrl(urls, node.imageSignedUrl);
    addUrl(urls, node.imageUrl);
    addUrl(urls, node.src);

    if (node.children) {
      walkFormStructure(node.children, urls);
    }
    if (node.elements) {
      walkFormStructure(node.elements, urls);
    }
    if (Array.isArray(node.rows)) {
      walkFormStructure(node.rows, urls);
    }
    if (Array.isArray(node.columns)) {
      walkFormStructure(node.columns, urls);
    }
  });
};

/**
 * Collect remote image URLs from a listfortemplate response.
 * @param {object} response
 * @returns {string[]}
 */
export const collectTemplateImageUrls = response => {
  const urls = new Set();
  if (!response) return [];

  addUrl(urls, response.requesterLogoUrl);

  (response.requestedDocuments || []).forEach(doc => {
    const attrs = doc?.attributes || {};
    (attrs.attachments || []).forEach(item => addUrl(urls, item));
    walkFormStructure(attrs.formStructure, urls);
  });

  return Array.from(urls);
};

const fetchImagesAsBlobs = async urls => {
  const images = {};

  await Promise.all(
    urls.map(async url => {
      try {
        const blob = await createBlobFromUrl(url);
        if (blob && blob.size > 0) {
          images[url] = blob;
        }
      } catch (error) {
        console.warn('Failed to cache template image', url, error);
      }
    })
  );

  return images;
};

const rewriteUrlsDeep = (value, urlMap) => {
  if (typeof value === 'string') {
    return urlMap[value] || value;
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(item => rewriteUrlsDeep(item, urlMap));
  }

  const result = {};
  Object.keys(value).forEach(key => {
    result[key] = rewriteUrlsDeep(value[key], urlMap);
  });
  return result;
};

const cloneJson = value => JSON.parse(JSON.stringify(value));

/**
 * Persist full listfortemplate response + image blobs for offline standalone use.
 * @param {string} templateId
 * @param {object} response
 */
export const saveCachedTemplate = async (templateId, response) => {
  if (!templateId || !response || response.errorCode) return;

  try {
    const imageUrls = collectTemplateImageUrls(response);
    const images = await fetchImagesAsBlobs(imageUrls);

    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await idbRequest(
      tx.objectStore(STORE_NAME).put({
        id: templateId,
        response,
        images,
        updatedAt: Date.now()
      })
    );
    gaeApp(
      'TEMPLATE_CACHE',
      'SAVE',
      `${templateId}:images=${Object.keys(images).length}/${imageUrls.length}`
    );
  } catch (error) {
    // Cache failures must not break the online flow.
    gaeApp('TEMPLATE_CACHE', 'SAVE_ERROR', templateId);
    console.warn('Failed to cache template', templateId, error);
  }
};

/**
 * @param {string} templateId
 * @returns {Promise<object|null>} cached listfortemplate response with local image URLs
 */
export const getCachedTemplate = async templateId => {
  if (!templateId) return null;

  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const record = await idbRequest(tx.objectStore(STORE_NAME).get(templateId));
    if (!record?.response) {
      gaeApp('TEMPLATE_CACHE', 'LOAD_MISS', templateId);
      return null;
    }

    const urlMap = {};
    const storedImages = record.images || {};
    Object.keys(storedImages).forEach(url => {
      const blob = storedImages[url];
      if (blob) {
        urlMap[url] = URL.createObjectURL(blob);
      }
    });

    const hydrated =
      Object.keys(urlMap).length > 0
        ? rewriteUrlsDeep(cloneJson(record.response), urlMap)
        : record.response;

    gaeApp(
      'TEMPLATE_CACHE',
      'LOAD_HIT',
      `${templateId}:images=${Object.keys(urlMap).length}`
    );
    return hydrated;
  } catch (error) {
    gaeApp('TEMPLATE_CACHE', 'LOAD_ERROR', templateId);
    console.warn('Failed to read cached template', templateId, error);
    return null;
  }
};

/**
 * @param {string|string[]} templateIds
 */
export const removeCachedTemplates = async templateIds => {
  const ids = (Array.isArray(templateIds) ? templateIds : [templateIds]).filter(
    Boolean
  );
  if (!ids.length) return;

  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await Promise.all(ids.map(id => idbRequest(store.delete(id))));
    gaeApp('TEMPLATE_CACHE', 'REMOVE', ids.join(','));
  } catch (error) {
    gaeApp('TEMPLATE_CACHE', 'REMOVE_ERROR', ids.join(','));
    console.warn('Failed to remove cached templates', ids, error);
  }
};

export const isNetworkUnavailable = error =>
  !isAppOnline() ||
  error?.message === 'Network Error' ||
  error?.code === 'ERR_NETWORK';

/**
 * API listfortemplate payload when the template id is unknown / deleted.
 * @param {object} response
 * @returns {boolean}
 */
export const isTemplateNotFoundResponse = response => {
  if (!response) return false;
  const text = [
    response.infoMessage,
    response.errorMessage,
    response.error,
    response.errorCode,
    response.message
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    text.indexOf('does not exist') !== -1 ||
    text.indexOf('not exist') !== -1 ||
    text.indexOf('not found') !== -1
  );
};
